// src/components/Contact.js
import React, { useState } from "react";
import "./Contact.css";
import { ClipLoader } from "react-spinners";
import Popup from "./Popup";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    dateOfBirth: "",
    phoneNumber: "",
    emailAddress: "",
    reasonForConsultation: "",
    diagnosedConditions: "",
    suicideHistory: "",
    suicidalThoughts: "",
    currentMedications: "",
    consent: false,
    smsConsent: false,
  });

  const togglePopup = () => setIsPopupOpen(!isPopupOpen);
  const toggleForm = () => setIsFormOpen(!isFormOpen);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    const loaderTimer = setTimeout(() => {
      if (isSubmitting) setShowLoader(true);
    }, 1500);

    try {
      const response = await fetch("https://mindspace-backend-jbux.onrender.com/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        console.log("✅ Email sent successfully");
        clearTimeout(loaderTimer);
        setIsSubmitting(false);
        setShowLoader(false);
        togglePopup();
      } else {
        throw new Error("Email sending failed");
      }
    } catch (error) {
      console.error("❌ Error sending email:", error);
      clearTimeout(loaderTimer);
      setIsSubmitting(false);
      setShowLoader(false);
      alert("Error: " + error.message);
    }
  };

  return (
    <div id="contact" className="contact-section">
      <div className="contact-form-container">
        <div className={`form-header ${isFormOpen ? "active" : ""}`} onClick={toggleForm}>
          <div className="header-content">
            <h2 className="form-title">Registration Form</h2>
            <div className={`dropdown-arrow ${isFormOpen ? "rotate" : ""}`}>
              <ChevronDown size={22} />
            </div>
          </div>
          <p className="form-description">
            {isFormOpen
              ? "Fill out this form to provide the necessary information for your appointment."
              : "Click to open the registration form and book your appointment."}
          </p>
        </div>

        <div className={`form-content ${isFormOpen ? "open" : ""}`}>
          {showLoader ? (
            <div className="loader-container">
              <ClipLoader color="#36d7b7" loading={showLoader} size={50} />
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit}>
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />

              <label htmlFor="dateOfBirth">Date of Birth *</label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                required
              />

              <label htmlFor="phoneNumber">Phone Number *</label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                required
              />

              <label htmlFor="emailAddress">Email Address *</label>
              <input
                type="email"
                id="emailAddress"
                name="emailAddress"
                value={formData.emailAddress}
                onChange={handleChange}
                required
              />

              <label htmlFor="reasonForConsultation">Reason for Consultation *</label>
              <textarea
                id="reasonForConsultation"
                name="reasonForConsultation"
                value={formData.reasonForConsultation}
                onChange={handleChange}
                rows="4"
                required
              ></textarea>

              <label htmlFor="diagnosedConditions">
                Have you been diagnosed with any psychiatric conditions in the past? *
              </label>
              <textarea
                id="diagnosedConditions"
                name="diagnosedConditions"
                value={formData.diagnosedConditions}
                onChange={handleChange}
                rows="4"
                required
              ></textarea>

              <label>Do you have any history of suicide attempts or self-harm? *</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="suicideHistory"
                    value="yes"
                    checked={formData.suicideHistory === "yes"}
                    onChange={handleChange}
                    required
                  />{" "}
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="suicideHistory"
                    value="no"
                    checked={formData.suicideHistory === "no"}
                    onChange={handleChange}
                    required
                  />{" "}
                  No
                </label>
              </div>

              <label>Are you currently having any suicidal thoughts? *</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="suicidalThoughts"
                    value="yes"
                    checked={formData.suicidalThoughts === "yes"}
                    onChange={handleChange}
                    required
                  />{" "}
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="suicidalThoughts"
                    value="no"
                    checked={formData.suicidalThoughts === "no"}
                    onChange={handleChange}
                    required
                  />{" "}
                  No
                </label>
              </div>

              <label htmlFor="currentMedications">
                List any medications you are taking currently *
              </label>
              <textarea
                id="currentMedications"
                name="currentMedications"
                value={formData.currentMedications}
                onChange={handleChange}
                rows="4"
                required
              ></textarea>

              {/* Consent Checkbox 1 */}
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  id="consent"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="consent">
                  I agree to participate in telepsychiatry sessions.
                </label>
              </div>

              {/* ✅ New Consent Checkbox 2 */}
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  id="smsConsent"
                  name="smsConsent"
                  checked={formData.smsConsent}
                  onChange={handleChange}

                />
                <label htmlFor="smsConsent">
                  By checking this box, I agree to receive text messages from Mindspace Pyschiatric Services PLLC related to online appointment booking and promotional and payment at the phone number provided above. The SMS frequency may vary. Data rates may apply. For assistance reply HELP. Reply STOP to opt out of receiving text messages.
                  Please review our{" "}
                  <Link to='/privacy-policy'>Privacy Policy</Link>
                  <span> and </span>
                  <Link to='/terms-of-use'>Terms of Use</Link>
                </label>
              </div>
              <button type="submit" className="submit-button">
                Submit Details
              </button>
            </form>
          )}
        </div>

        <div className="disclaimer-section">
          <p className="disclaimer-title">* Disclaimer</p>
          <p>
            If you or someone you know is struggling with suicide or in crisis, help is available. Call or text 988 or visit the <a href="https://988lifeline.org/chat/" target="_blank" rel="noopener noreferrer">Lifeline Chat</a> to connect with a available trained crisis counselor, or Call 911 or Call or text 988.
          </p>
          <p>
            *Do not rely on electronic communications or messaging communication through our website for immediate, urgent or emergent medical/psychiatric needs. Our website is not designed to facilitate medical/psychiatric emergencies or crisis care, our providers are not available for urgent or emergent or crisis care.
          </p>
        </div>

        {isPopupOpen && <Popup togglePopup={togglePopup} />}
      </div>
    </div>
  );
};

export default Contact;
